import {
    type CreateSupportTicketResponse,
    getSupabaseAccessToken,
    getSupportFunctionUrl,
    SupportTicketError,
} from '@/services/supportTicket';

export type FeedbackSeverity = 'low' | 'normal' | 'important' | 'urgent';

export interface VisualFeedbackPayload {
    issue_type: 'visual_feedback';
    feedback: string;
    target_mode: 'page' | 'element';
    severity: FeedbackSeverity;
    domain: string;
    page_url: string;
    reporter_name: string;
    reporter_email: string;
    environment: string;
    app_version: string;
    viewport_width: number;
    viewport_height: number;
    element_selector?: string;
    element_context?: string;
    screenshot_filename: string;
    screenshot_content_type: string;
    screenshot_base64: string;
}

/** Decoded size of a base64 string, without allocating the bytes. */
export function base64ByteLength(base64: string): number {
    const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
    return Math.floor((base64.length * 3) / 4) - padding;
}

export const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024;

export async function createVisualFeedback(
    payload: VisualFeedbackPayload
): Promise<CreateSupportTicketResponse> {
    if (base64ByteLength(payload.screenshot_base64) > MAX_SCREENSHOT_BYTES) {
        throw new SupportTicketError('Screenshot is too large to send. Try a smaller viewport.');
    }

    const functionUrl = getSupportFunctionUrl();
    const accessToken = await getSupabaseAccessToken();

    let response: Response;
    try {
        response = await fetch(functionUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ payload }),
        });
    } catch (fetchError) {
        const message = fetchError instanceof Error ? fetchError.message : 'Unknown network error';
        throw new SupportTicketError(`Network error: ${message}`);
    }

    if (!response.ok) {
        let detail: string | null = null;
        try {
            const body = (await response.json()) as { error?: string };
            detail = body.error ?? null;
        } catch {
            // ignore parse errors
        }
        throw new SupportTicketError(
            `Failed to send feedback (${response.status})${detail ? `: ${detail}` : ''}`,
            response.status
        );
    }

    try {
        return (await response.json()) as CreateSupportTicketResponse;
    } catch {
        throw new SupportTicketError('Failed to parse response');
    }
}
